import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import { workflowApi } from '../lib/api';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import SimpleBlockList from '../components/workflow/SimpleWorkflowSteps';
import {
  compileSimpleWorkflow,
  templateToSimple,
  emptySimpleWorkflow,
  newTask,
} from '../lib/workflowSimple';
import {
  ArrowLeft, FloppyDisk, CircleNotch, TreeStructure, Warning,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function SimpleWorkflowDesignerPage() {
  const { templateId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const assignProductId = searchParams.get('assignProduct') || '';

  const [name, setName] = useState(searchParams.get('name') || '');
  const [description, setDescription] = useState('');
  const [blocks, setBlocks] = useState(emptySimpleWorkflow().blocks);
  const [advancedDetected, setAdvancedDetected] = useState(false);
  const [loading, setLoading] = useState(!!templateId);
  const [saving, setSaving] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState(() =>
    JSON.stringify({
      name: (searchParams.get('name') || '').trim(),
      description: '',
      blocks: emptySimpleWorkflow().blocks,
    }),
  );

  useEffect(() => {
    if (templateId) loadTemplate();
  }, [templateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTemplate = async () => {
    try {
      const res = await workflowApi.getTemplate(templateId);
      const simple = templateToSimple(res.data);
      const nextName = simple.name;
      const nextDesc = simple.description;
      const nextBlocks = simple.blocks?.length ? simple.blocks : [newTask()];
      setName(nextName);
      setDescription(nextDesc);
      setBlocks(nextBlocks);
      setAdvancedDetected(simple.advanced);
      setSavedSnapshot(JSON.stringify({ name: nextName, description: nextDesc, blocks: nextBlocks }));
    } catch {
      toast.error('Failed to load workflow');
    } finally {
      setLoading(false);
    }
  };

  const isDirty =
    savedSnapshot !==
    JSON.stringify({ name: name.trim(), description: description.trim(), blocks });

  const validateBlocks = () => {
    const check = (items) => {
      for (const b of items || []) {
        if (b.type === 'task' && !b.label?.trim()) {
          return 'Every step needs a name';
        }
        if (b.type === 'split') {
          for (const br of b.branches || []) {
            const err = check(br.steps);
            if (err) return err;
          }
        }
        if (b.type === 'parallel') {
          for (const lane of b.lanes || []) {
            const err = check(lane.steps);
            if (err) return err;
          }
        }
        if (b.type === 'subprocess') {
          const err = check(b.steps);
          if (err) return err;
        }
      }
      return null;
    };
    return check(blocks);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Enter a workflow name');
      return;
    }
    const validationError = validateBlocks();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    if (!blocks.length) {
      toast.error('Add at least one step');
      return;
    }

    setSaving(true);
    try {
      const { nodes, edges } = compileSimpleWorkflow(blocks);
      const payload = {
        name: name.trim(),
        description: description.trim(),
        nodes,
        edges,
      };
      let savedId = templateId;
      if (templateId) {
        await workflowApi.updateTemplate(templateId, payload);
        toast.success('Workflow updated');
      } else {
        const res = await workflowApi.createTemplate(payload);
        savedId = res.data.id;
        toast.success('Workflow saved');
      }
      if (assignProductId && savedId) {
        await workflowApi.assignToProduct(assignProductId, savedId);
        toast.success('Workflow assigned to product');
        navigate('/products');
      } else if (!templateId && savedId) {
        navigate(`/workflows/${savedId}`, { replace: true });
      }
      setSavedSnapshot(
        JSON.stringify({
          name: payload.name,
          description: payload.description,
          blocks,
        }),
      );
      setSaveDialogOpen(false);
      setAdvancedDetected(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const canvasPath = templateId
    ? `/workflows/canvas/${templateId}`
    : `/workflows/canvas/new${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6 pb-10" data-testid="simple-workflow-designer">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-3">
            <button type="button" onClick={() => navigate('/workflows')}
              className={`mt-0.5 p-1.5 rounded-lg transition-colors ${isDark ? 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800' : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100'}`}
              data-testid="back-to-workflows">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className={`text-2xl font-heading font-semibold ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>
                {templateId ? 'Edit workflow' : 'New workflow'}
              </h1>
              <p className={`mt-1 text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                Build steps in order — split, parallel, or sub-process blocks compile to the BPM engine.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => navigate(canvasPath)}
              className={isDark ? 'border-zinc-700 text-zinc-300' : ''} data-testid="open-advanced-canvas">
              <TreeStructure className="w-4 h-4 mr-1.5" /> Advanced canvas
            </Button>
            <Button type="button" size="sm" onClick={() => setSaveDialogOpen(true)}
              disabled={Boolean(templateId) && !isDirty}
              className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="save-workflow-btn">
              <FloppyDisk className="w-4 h-4 mr-1.5" /> Save
            </Button>
          </div>
        </div>

        {assignProductId && (
          <div className={`rounded-lg border px-4 py-3 text-sm ${isDark ? 'bg-brand-500/10 border-brand-500/20 text-brand-300' : 'bg-brand-50 border-brand-200 text-brand-700'}`}>
            This workflow will be assigned to the product after save.
          </div>
        )}

        {advancedDetected && (
          <div className={`rounded-lg border px-4 py-3 flex gap-3 text-sm ${isDark ? 'bg-amber-500/10 border-amber-500/20 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
            <Warning className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Advanced workflow detected</p>
              <p className={`mt-1 text-xs ${isDark ? 'text-amber-300/80' : 'text-amber-700'}`}>
                Only part of this template could be loaded into the simple builder. Saving will replace the full graph with the steps shown below, or use Advanced canvas to edit the original layout.
              </p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><CircleNotch className="w-8 h-8 animate-spin text-brand-500" /></div>
        ) : (
          <>
            <div className={`rounded-xl border p-4 space-y-4 ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={`text-xs font-medium ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>Workflow name *</label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. KSDL Inspect & Pack"
                    className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''}`}
                    data-testid="workflow-name-input" />
                </div>
                <div>
                  <label className={`text-xs font-medium ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>Description</label>
                  <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional summary"
                    className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''}`}
                    data-testid="workflow-desc-input" />
                </div>
              </div>
            </div>

            <div className={`rounded-xl border p-4 ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <h2 className={`text-sm font-semibold ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>Steps</h2>
                <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                  Start → {blocks.length} block{blocks.length !== 1 ? 's' : ''} → End
                </span>
              </div>
              <SimpleBlockList blocks={blocks} onChange={setBlocks} isDark={isDark} />
            </div>
          </>
        )}

        <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
          <DialogContent className={`${isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-100' : 'bg-white border-zinc-200'} max-w-md`}>
            <DialogHeader>
              <DialogTitle className="font-heading">{templateId ? 'Update workflow' : 'Save workflow'}</DialogTitle>
              <DialogDescription className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>
                {blocks.length} step block{blocks.length !== 1 ? 's' : ''} will be compiled to the workflow engine format.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className={`text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>Name *</label>
                <Input value={name} onChange={(e) => setName(e.target.value)}
                  className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''}`} />
              </div>
              <div>
                <label className={`text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>Description</label>
                <Input value={description} onChange={(e) => setDescription(e.target.value)}
                  className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''}`} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSaveDialogOpen(false)} className={isDark ? 'border-zinc-700 text-zinc-300' : ''}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving || !name.trim()} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="confirm-save-workflow">
                {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
